import { useEffect, useState } from "react";
import axios from "axios";
import { Lock, LogOut } from "lucide-react";
import { GoldDivider } from "@/components/wedding/Ornaments";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KA_MONTHS = [
  "იანვარი", "თებერვალი", "მარტი", "აპრილი", "მაისი", "ივნისი",
  "ივლისი", "აგვისტო", "სექტემბერი", "ოქტომბერი", "ნოემბერი", "დეკემბერი",
];

const formatDate = (iso) => {
  const d = new Date(iso);
  return `${d.getDate()} ${KA_MONTHS[d.getMonth()]}`;
};

const Stat = ({ label, value, testid }) => (
  <div className="gold-frame bg-white px-3 py-4 text-center" data-testid={testid}>
    <p className="font-display gold-text text-2xl tabular-nums">{value}</p>
    <p className="mt-1 text-[10px] uppercase tracking-[0.15em] text-[#A1A1AA]">{label}</p>
  </div>
);

export default function AdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem("admin-token") || "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [rsvps, setRsvps] = useState(null);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/rsvp`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setRsvps(data);
      } catch {
        sessionStorage.removeItem("admin-token");
        setToken("");
      }
    })();
  }, [token]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      sessionStorage.setItem("admin-token", data.token);
      setToken(data.token);
    } catch {
      setError("პაროლი არასწორია");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem("admin-token");
    setToken("");
    setRsvps(null);
    setPassword("");
  };

  if (!token) {
    return (
      <div className="paper-bg flex min-h-screen items-center justify-center px-5" data-testid="admin-page">
        <form
          onSubmit={handleLogin}
          data-testid="admin-login-form"
          className="gold-frame w-full max-w-xs space-y-5 bg-white p-8 text-center"
        >
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-[#E5D5B8] bg-[#FFF9EA] text-[#B8860B]">
            <Lock size={18} />
          </span>
          <div>
            <h1 className="text-lg font-medium text-[#292524]">ადმინ გვერდი</h1>
            <p className="mt-1 text-xs text-[#A1A1AA]">დასწრების დადასტურებების ცხრილი</p>
          </div>
          <input
            type="password"
            data-testid="admin-password-input"
            className="input-elegant text-center"
            placeholder="პაროლი"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
          />
          {error && (
            <p data-testid="admin-login-error" className="text-sm text-[#B91C1C]">
              {error}
            </p>
          )}
          <button
            data-testid="admin-login-button"
            type="submit"
            disabled={submitting}
            className="btn-gold w-full rounded-full py-3 text-sm font-semibold tracking-wide disabled:opacity-60"
          >
            {submitting ? "იტვირთება..." : "შესვლა"}
          </button>
        </form>
      </div>
    );
  }

  const attending = rsvps?.filter((r) => r.attending) ?? [];
  const declined = rsvps?.filter((r) => !r.attending) ?? [];
  const guestTotal = attending.reduce((sum, r) => sum + r.guests, 0);

  return (
    <div className="paper-bg min-h-screen px-4 py-10" data-testid="admin-page">
      <div className="mx-auto max-w-2xl">
        <div className="mb-8 text-center">
          <p className="eyebrow">რობერტი & დიანა</p>
          <h1 className="mt-2 text-2xl font-medium text-[#292524]">დასწრების დადასტურებები</h1>
          <div className="mt-3">
            <GoldDivider />
          </div>
        </div>

        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="admin-stats">
          <Stat label="პასუხები" value={rsvps?.length ?? 0} testid="admin-stat-total" />
          <Stat label="მოვლენ" value={attending.length} testid="admin-stat-attending" />
          <Stat label="ვერ მოვლენ" value={declined.length} testid="admin-stat-declined" />
          <Stat label="სულ სტუმარი" value={guestTotal} testid="admin-stat-guests" />
        </div>

        <div className="gold-frame overflow-x-auto bg-white" data-testid="admin-table-wrapper">
          <table className="w-full min-w-[560px] text-sm" data-testid="admin-table">
            <thead>
              <tr className="border-b border-[#EFE6D2] text-left text-[10px] uppercase tracking-[0.15em] text-[#A1A1AA]">
                <th className="px-4 py-3">#</th>
                <th className="py-3">სახელი და გვარი</th>
                <th className="py-3">სტატუსი</th>
                <th className="py-3 text-center">სტუმრები</th>
                <th className="py-3 pr-4 text-right">თარიღი</th>
              </tr>
            </thead>
            <tbody>
              {(rsvps ?? []).map((r, i) => (
                <tr
                  key={r.id}
                  data-testid={`admin-rsvp-row-${i}`}
                  className="border-b border-[#F5F0E4] last:border-0"
                >
                  <td className="px-4 py-3 text-[#A1A1AA]">{i + 1}</td>
                  <td className="py-3 font-medium text-[#292524]">{r.name}</td>
                  <td className="py-3">
                    <span
                      className={`inline-block rounded-full border px-2.5 py-0.5 text-[11px] ${
                        r.attending
                          ? "border-[#D4AF37] bg-[#FFF9EA] text-[#8C6515]"
                          : "border-[#E7E0D0] bg-[#F5F5F4] text-[#78716C]"
                      }`}
                    >
                      {r.attending ? "მოვდივარ" : "ვერ მოვდივარ"}
                    </span>
                  </td>
                  <td className="py-3 text-center tabular-nums text-[#44403C]">{r.guests}</td>
                  <td className="py-3 pr-4 text-right text-[#78716C]">{formatDate(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {rsvps !== null && rsvps.length === 0 && (
            <p className="p-10 text-center text-sm text-[#A1A1AA]">ჯერ პასუხები არ არის</p>
          )}
          {rsvps === null && (
            <p className="p-10 text-center text-sm text-[#A1A1AA]">იტვირთება...</p>
          )}
        </div>

        <button
          data-testid="admin-logout-button"
          onClick={handleLogout}
          className="mx-auto mt-8 flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#B8860B] underline-offset-4 hover:underline"
        >
          <LogOut size={13} />
          გამოსვლა
        </button>
      </div>
    </div>
  );
}
